import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { billingApi } from '../services/billingApi';
import { usePractice } from '../contexts/PracticeContext';
import type { AuditEventDto, Page } from '../types/billing';

type ChipColor = 'default' | 'info' | 'primary' | 'error';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const sec = Math.max(0, Math.floor(diffMs / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day <= 30) return `${day}d ago`;
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function actionChipColor(action: string): ChipColor {
  if (/_FAILED$/.test(action) || action.startsWith('JOB_FAIL')) return 'error';
  if (action.startsWith('UPLOAD')) return 'info';
  if (action.startsWith('DISPATCH')) return 'primary';
  if (action.startsWith('CLAIM')) return 'default';
  return 'default';
}

const AuditLog: React.FC = () => {
  const { currentPracticeId } = usePractice();
  const [pageData, setPageData] = useState<Page<AuditEventDto> | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  const fetchPage = useCallback(
    async (practiceId: string, p: number, size: number) => {
      setIsLoading(true);
      try {
        const data = await billingApi.listAudit(practiceId, p, size);
        setPageData(data);
        setError(null);
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        setError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!currentPracticeId) return;
    void fetchPage(currentPracticeId, page, rowsPerPage);
  }, [currentPracticeId, page, rowsPerPage, fetchPage]);

  if (!currentPracticeId) {
    return <Alert severity="info">Select a practice first.</Alert>;
  }

  const reload = () => {
    if (!currentPracticeId) return;
    void fetchPage(currentPracticeId, page, rowsPerPage);
  };

  const showInitialError = error !== null && pageData === null;
  const showEmpty =
    !isLoading && error === null && pageData !== null && pageData.content.length === 0;
  const showTable =
    !isLoading && pageData !== null && pageData.content.length > 0;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Audit log
      </Typography>

      {isLoading && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Who</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Target type</TableCell>
                <TableCell>Target id</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                  <TableCell><Skeleton variant="text" animation="wave" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {showInitialError && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Could not load audit events</AlertTitle>
          {error}
        </Alert>
      )}

      {showEmpty && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
          <InboxOutlinedIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            No audit events yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Audit events are recorded automatically when you upload, dispatch, or modify claims.
          </Typography>
        </Paper>
      )}

      {showTable && pageData !== null && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Who</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Target type</TableCell>
                  <TableCell>Target id</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageData.content.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Tooltip title={new Date(row.createdAt).toLocaleString()}>
                        <span>{relativeTime(row.createdAt)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.actorEmail ?? row.actorUserId}>
                        <span>{row.actorName ?? row.actorEmail ?? row.actorCognitoSub.slice(0, 8)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.action}
                        size="small"
                        variant="outlined"
                        color={actionChipColor(row.action)}
                      />
                    </TableCell>
                    <TableCell>{row.targetType}</TableCell>
                    <TableCell>
                      <Tooltip title={row.targetId}>
                        <span>{row.targetId.slice(0, 8)}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pageData.totalElements}
            page={pageData.number}
            rowsPerPage={pageData.size}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default AuditLog;
